import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { CursorCandles } from "@/components/CursorCandles";
import { Crosshair } from "@/components/Crosshair";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { TerminalReadout } from "@/components/TerminalReadout";
import { ProfileCard } from "@/components/rank/ProfileCard";
import { ErrorPanel, LoadingPanel } from "@/components/rank/States";
import { PagedPeople, SimplePeople } from "@/components/rank/PeopleList";
import {
  checkFollow,
  fetchAbout,
  fetchFollowers,
  fetchFollowing,
  fetchNewFollowers7d,
  fetchNewFollowing7d,
  fetchRankProfile,
  fetchVerifiedFollowers,
  FRIENDLY_API_ERROR,
} from "@/lib/api";
import type { RankProfile, About } from "@/lib/types";

export const Route = createFileRoute("/profile/$handle")({
  head: ({ params }) => {
    const h = params.handle;
    return {
      meta: [
        { title: `@${h} — Hoodrank profile, followers and trust signals` },
        {
          name: "description",
          content: `Full Hoodrank breakdown for @${h}: score, follower and following lists, verified followers, 7-day graph changes and trust metadata.`,
        },
        { property: "og:title", content: `@${h} on Hoodrank` },
        {
          property: "og:description",
          content: `Score, follower quality, 7D graph changes and trust signals for @${h}.`,
        },
        { property: "og:type", content: "profile" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: ProfilePage,
});

const TABS = [
  "Overview",
  "Followers",
  "Following",
  "Verified",
  "New 7D",
  "Trust",
] as const;
type Tab = (typeof TABS)[number];

function FollowCheck({ handle }: { handle: string }) {
  const [other, setOther] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await checkFollow(handle, other);
      if (r.user_protected) {
        setResult("Account is protected — follow graph unavailable.");
      } else {
        setResult(
          r.follow
            ? `@${other.replace(/^@/, "")} follows @${handle}`
            : `@${other.replace(/^@/, "")} does not follow @${handle}`,
        );
      }
    } catch {
      setResult(FRIENDLY_API_ERROR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-ink bg-bg-panel">
      <div className="border-b border-line px-5 py-3">
        <p className="label">Check follow</p>
      </div>
      <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center">
        <input
          value={other}
          onChange={(e) => setOther(e.target.value)}
          placeholder="@kol_handle"
          className="mono min-w-0 flex-1 border border-ink bg-bg px-3 py-2 text-[13px] text-ink placeholder:text-ink-muted"
        />
        <button
          type="button"
          disabled={busy || !other.trim()}
          onClick={() => void run()}
          className="btn btn-dark disabled:opacity-50"
        >
          {busy ? "Checking…" : "Does they follow?"}
        </button>
      </div>
      {result ? (
        <p className="mono border-t border-line px-5 py-3 text-[12px] text-ink-soft">
          {result}
        </p>
      ) : null}
    </section>
  );
}

function TrustPanel({ handle }: { handle: string }) {
  const [about, setAbout] = useState<About | null>(null);

  useEffect(() => {
    let alive = true;
    fetchAbout(handle).then((a) => alive && setAbout(a));
    return () => {
      alive = false;
    };
  }, [handle]);

  if (!about) return <LoadingPanel label="Loading trust signals" />;

  const items = [
    { k: "Country", v: about.country ?? "Unknown" },
    { k: "Account age", v: `${about.account_age_days.toLocaleString()} days` },
    { k: "Account type", v: about.is_business_account ? "Business" : "Personal" },
    { k: "Bot likelihood", v: `${about.bot_likelihood}%` },
  ];

  return (
    <div className="space-y-8">
      <section className="border border-ink bg-bg-panel">
        <div className="border-b border-line px-5 py-3">
          <p className="label">Trust &amp; safety</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4">
          {items.map((it, i) => (
            <div
              key={it.k}
              className={`p-5 ${i > 0 ? "border-l border-line" : ""} ${
                i > 1 ? "border-t border-line md:border-t-0" : ""
              } ${i === 2 ? "border-l-0 md:border-l" : ""}`}
            >
              <p className="num text-[20px] font-semibold text-ink">{it.v}</p>
              <p className="label mt-1 text-[9px]">{it.k}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-line px-5 py-4">
          <p className="label">Username history</p>
          {about.username_changes.length === 0 ? (
            <p className="mono mt-3 text-[12px] text-ink-muted">
              No username changes recorded — handle has been stable.
            </p>
          ) : (
            <ul className="mt-3">
              {about.username_changes.map((c) => (
                <li
                  key={c.username}
                  className="flex items-center justify-between border-b border-line py-2"
                >
                  <span className="mono text-[12px] text-ink">@{c.username}</span>
                  <span className="mono text-[11px] text-ink-muted">
                    changed{" "}
                    {new Date(c.changed_at).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      <FollowCheck handle={handle} />
    </div>
  );
}

function ProfilePage() {
  const { handle } = Route.useParams();
  const [tab, setTab] = useState<Tab>("Overview");
  const [visited, setVisited] = useState(() => new Set<Tab>(["Overview"]));
  const [profile, setProfile] = useState<RankProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useMemo(
    () => (force = false) => {
      setError(null);
      if (force) setProfile(null);
      fetchRankProfile(handle, { force })
        .then(setProfile)
        .catch((e) =>
          setError(e instanceof Error ? e.message : "Failed to load profile"),
        );
    },
    [handle],
  );

  useEffect(() => {
    setTab("Overview");
    setVisited(new Set<Tab>(["Overview"]));
    setProfile(null);
    setError(null);
    load(false);
  }, [handle, load]);

  useEffect(() => {
    setVisited((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, [tab]);

  useEffect(() => {
    if (!profile) return;
    document.body.dataset.tier = profile.tier;
    return () => {
      document.body.dataset.tier = "elite";
    };
  }, [profile]);

  return (
    <>
      <CursorCandles />
      <Crosshair />
      <SiteHeader />

      <main data-candles className="grid-paper min-h-[calc(100vh-4rem)]">
        <div className="shell py-14 md:py-20">
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.85fr)] lg:gap-12">
            <div>
              <p className="label mb-6">Profile · Hoodrank</p>
              <h1 className="max-w-[16ch] !text-[clamp(34px,9vw,84px)] break-words">
                @<span className="hollow">{handle}</span>
              </h1>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link
                  to="/feed/$handle"
                  params={{ handle }}
                  className="btn btn-dark w-full sm:w-auto"
                >
                  Posts &amp; mentions →
                </Link>
                <Link to="/app" className="btn btn-outline w-full sm:w-auto">
                  Score another handle
                </Link>
              </div>
            </div>

            <TerminalReadout
              phase={error ? "error" : profile ? "ready" : "loading"}
              profile={profile}
              handle={handle}
              className="border border-ink bg-bg-panel"
            />
          </div>

          <nav className="tab-strip mt-10 -mx-5 px-5 sm:mx-0 sm:flex-wrap sm:px-0" aria-label="Profile sections">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                aria-current={tab === t}
                className={`mono border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
                  tab === t
                    ? "border-ink bg-accent text-accent-ink"
                    : "border-line text-ink-soft hover:border-ink hover:text-ink"
                }`}
              >
                {t}
              </button>
            ))}
          </nav>

          <div className="mt-8 space-y-8">
            {error ? (
              <ErrorPanel message={error} onRetry={() => load(true)} />
            ) : null}

            {visited.has("Overview") ? (
              <div hidden={tab !== "Overview"}>
                {!error && !profile ? (
                  <LoadingPanel />
                ) : profile ? (
                  <ProfileCard key={handle} profile={profile} />
                ) : null}
              </div>
            ) : null}

            {visited.has("Followers") ? (
              <div hidden={tab !== "Followers"}>
                <PagedPeople
                  key={`${handle}-followers`}
                  title="Followers"
                  hint="No followers indexed for this handle yet."
                  load={(page) => fetchFollowers(handle, page)}
                />
              </div>
            ) : null}

            {visited.has("Following") ? (
              <div hidden={tab !== "Following"}>
                <PagedPeople
                  key={`${handle}-following`}
                  title="Following"
                  hint="This handle follows nobody in the indexed graph."
                  load={(page) => fetchFollowing(handle, page)}
                />
              </div>
            ) : null}

            {visited.has("Verified") ? (
              <div hidden={tab !== "Verified"}>
                <PagedPeople
                  key={`${handle}-verified`}
                  title="Verified followers"
                  hint="No verified accounts follow this handle yet."
                  load={(page) => fetchVerifiedFollowers(handle, page)}
                />
              </div>
            ) : null}

            {visited.has("New 7D") ? (
              <div hidden={tab !== "New 7D"} className="space-y-8">
                <SimplePeople
                  key={`${handle}-new-followers`}
                  title="New followers"
                  hint="No new followers in the last 7 days. This list only covers crypto-indexed accounts in our database — not every X follow."
                  load={() => fetchNewFollowers7d(handle)}
                />
                <SimplePeople
                  key={`${handle}-new-following`}
                  title="New following"
                  hint="No new follows in the last 7 days. Only crypto-indexed accounts are tracked here — not every X follow."
                  load={() => fetchNewFollowing7d(handle)}
                />
              </div>
            ) : null}

            {visited.has("Trust") ? (
              <div hidden={tab !== "Trust"}>
                <TrustPanel key={handle} handle={handle} />
              </div>
            ) : null}
          </div>

          <p className="mono mt-6 text-[12px] text-ink-muted">
            Profile, followers, following, verified followers and 7-day graph
            changes come from the indexed follower graph. Posts and mentions
            come from the user-tweets and mentions endpoints. New 7D lists cover
            crypto-indexed accounts only.
          </p>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}
